import React, { useState, FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { sendResetPassword } from '../../backendApi/backend';
import { 
  Input, 
  Button, 
  Typography,
  Row,
  Col,
  notification,
  Card
} from 'antd';
import { 
  MailOutlined, 
  ArrowLeftOutlined,
  SendOutlined
} from '@ant-design/icons';

import './ForgotPassword.css';

const { Title, Text, Paragraph } = Typography;

const ForgotPassword: FunctionComponent = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      notification.error({
        message: 'Error',
        description: 'Please enter your email address.',
      });
      return;
    }

    setLoading(true);
    
    try {
      
        const response = await sendResetPassword(email)
        console.log("Response from sendResetPassword:", response);
        if (response.status !== 200) {
            notification.error({
                message: 'Error',
                description: 'Failed to send reset instructions. Please try again later.',
            });
        }
        else {
            setIsSubmitted(true);
            notification.success({
                message: 'Email Sent',
                description: 'If an account with that email exists, we\'ve sent password reset instructions.',
            });
        }

    } catch (error: any) {
      console.error("Forgot password request failed:", error);
      
      if (error.response?.data?.detail) {
        notification.error({
          message: 'Error',
          description: error.response.data.detail,
        });
      } else {
        notification.error({
          message: 'Error',
          description: 'Something went wrong. Please try again later.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="forgot-password-page-container">
        <Row justify="center" align="middle" className="forgot-password-row">
          <Col xs={22} sm={20} md={20} lg={20} xl={15} className="forgot-password-form-column">
            <div className="forgot-password-form-container">
              <div className="forgot-password-header text-center">
                <div className="success-icon">
                  <SendOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
                </div>
                <Title level={2} className="forgot-password-title">Check Your Email</Title>
                <Paragraph className="forgot-password-subtitle">
                  We've sent password reset instructions to <strong>{email}</strong>
                </Paragraph>
                <Paragraph className="forgot-password-subtitle">
                  Check your email and follow the link to reset your password. 
                  If you don't see the email, check your spam folder.
                </Paragraph>
              </div>
              
              <div className="forgot-password-actions">
                <Link to="/login" className="back-to-login-link">
                  <Button 
                    type="primary" 
                    size="large" 
                    className="back-to-login-button"
                    icon={<ArrowLeftOutlined />}
                  >
                    Back to Login
                  </Button>
                </Link>
                
                <Button 
                  type="link" 
                  size="large" 
                  className="resend-button"
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                >
                  Try a different email
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="forgot-password-page-container">
      <Row justify="center" align="middle" className="forgot-password-row">
        <Col xs={22} sm={20} md={20} lg={20} xl={15} className="forgot-password-form-column">
          <div className="forgot-password-form-container">
            <div className="forgot-password-header text-center">
              <Title level={2} className="forgot-password-title">Forgot Password?</Title>
              <Paragraph className="forgot-password-subtitle">
                Enter your email address and we'll send you instructions to reset your password.
              </Paragraph>
            </div>
            
            <form className="forgot-password-form" onSubmit={handleSubmit}>
              <div className="form-input-group">
                <label htmlFor="email" className="input-label">Email Address</label>
                <Input
                  id="email"
                  size="large"
                  type="email"
                  placeholder="Enter your email address"
                  prefix={<MailOutlined className="input-icon" />}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="forgot-password-input"
                  disabled={loading}
                />
              </div>
              
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                className="forgot-password-button"
                icon={<SendOutlined />}
                loading={loading}
                disabled={loading}
              >
                Send Reset Instructions
              </Button>
              
              <div className="back-to-login text-center">
                <Link to="/login" className="back-to-login-link">
                  <ArrowLeftOutlined /> Back to Login
                </Link>
              </div>
            </form>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ForgotPassword;
